import { useMemo } from "react";

import { DateTime } from "luxon";
import {
  ArrowTopRightOnSquareIcon,
  BuildingOffice2Icon,
  CreditCardIcon,
  TruckIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

import Card from "../../shared/Card/Card";
import LoadingWheel from "../../shared/LoadingWheel";
import useAccountContacts from "../../../data/useAccountContacts";
import { useAddressLookup } from "../../../hooks/useAddressLookup";
import getAddress from "../../../utils/getAddress";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import { IPart } from "../../../models/Part";
import { IInvoice, InvoiceStatus, PaymentMethod } from "../../../models/invoice/IInvoice";
import Field from "./StockField";

const invoiceStatusLabel: Record<InvoiceStatus, { label: string; tone: string }> = {
  [InvoiceStatus.Draft]: { label: "Draft", tone: "bg-gray-100 text-gray-700 ring-gray-200" },
  [InvoiceStatus.Approved]: { label: "Approved", tone: "bg-blue-50 text-blue-700 ring-blue-200" },
  [InvoiceStatus.Paid]: { label: "Paid", tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  [InvoiceStatus.Credit]: { label: "Credit", tone: "bg-amber-50 text-amber-700 ring-amber-200" },
};

const paymentMethodLabel: Record<PaymentMethod, string> = {
  [PaymentMethod.Cash]: "Cash",
  [PaymentMethod.Card]: "Card",
  [PaymentMethod.Ebay]: "eBay",
  [PaymentMethod.Stripe]: "Stripe",
};

interface StockSalesInformationProps {
  part: IPart;
  invoice: IInvoice | null | undefined;
}

export default function StockSalesInformation({
  part,
  invoice,
}: StockSalesInformationProps) {
  const account = invoice?.account ?? null;
  const { accountContacts } = useAccountContacts(
    account?.id ?? "",
    "StockSalesInformation"
  );
  const { addressLookup, isLoading: addressLoading } = useAddressLookup(
    account?.id ?? ""
  );

  const primaryContact = useMemo(
    () =>
      account?.primaryContactId && !accountContacts.isLoading
        ? accountContacts.data?.data.find(
            (c) => c.id === account.primaryContactId
          )
        : null,
    [account?.primaryContactId, accountContacts.data, accountContacts.isLoading]
  );

  const billingAddress = useMemo(
    () =>
      account?.billingAddressId && !addressLoading
        ? addressLookup(account.billingAddressId)
        : null,
    [account?.billingAddressId, addressLoading, addressLookup]
  );

  const shippingAddress = useMemo(
    () =>
      account?.shippingAddressId && !addressLoading
        ? addressLookup(account.shippingAddressId)
        : null,
    [account?.shippingAddressId, addressLoading, addressLookup]
  );

  const partShippingAddress = useMemo(() => {
    const lines = [
      part.fullName,
      part.addressLine1,
      part.addressLine2,
      part.city,
      part.county,
      part.stateOrProvince,
      part.postcode,
    ].filter((l): l is string => !!l && l.trim() !== "");
    return lines.length > 0 ? lines.join(", ") : null;
  }, [part]);

  const status = invoice ? invoiceStatusLabel[invoice.status] : null;
  const balance = invoice
    ? Math.max(0, (invoice.invoiceTotal ?? 0) - (invoice.amountPaid ?? 0))
    : 0;

  const isSold = !!part.soldDate;

  if (!isSold && !invoice) {
    return (
      <Card bodyClassName="p-6 text-sm text-gray-500">
        <p>This part has not been sold yet.</p>
        <p className="mt-1 text-xs text-gray-400">
          Sales information will appear here once the part is checked out
          via the basket or marked as sold.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Order summary */}
      <Card title="Order Summary" bodyClassName="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Sold Date">
            {formatDateTime(part.soldDate, DateTime.DATETIME_SHORT, "Not Sold")}
          </Field>
          <Field label="Sold Price">
            {part.price ? PriceFormat(part.price) : "-"}
          </Field>
          <Field label="Order Number">{part.orderId ?? "-"}</Field>
          <Field label="Picked">
            {formatDateTime(part.pickedOn, DateTime.DATETIME_SHORT, "Not Picked")}
          </Field>
          <Field label="Shipped">
            {formatDateTime(part.shippedOn, DateTime.DATETIME_SHORT, "Not Shipped")}
          </Field>
          <Field label="Delivered">
            {formatDateTime(part.deliveredOn, DateTime.DATETIME_SHORT, "Not Delivered")}
          </Field>
          <Field label="Tracking #">{part.trackingNumber ?? "-"}</Field>
          <Field label="Shipping Address">
            {partShippingAddress ?? "-"}
          </Field>
        </div>
      </Card>

      {/* Invoice */}
      <Card
        title="Invoice"
        bodyClassName="p-4"
        headerRight={
          invoice ? (
            <a
              href={`/invoices/${invoice.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800"
            >
              View Invoice
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ) : undefined
        }
      >
        {invoice ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Invoice #">
              <span className="font-mono">{invoice.invoiceNumber}</span>
            </Field>
            <Field label="Status">
              {status && (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${status.tone}`}
                >
                  {status.label}
                </span>
              )}
            </Field>
            <Field label="Invoice Date">
              {formatDateTime(invoice.invoiceDate, DateTime.DATE_SHORT, "-")}
            </Field>
            <Field label="Due Date">
              {formatDateTime(invoice.dueDate, DateTime.DATE_SHORT, "-")}
            </Field>
            <Field label="Subtotal">{PriceFormat(invoice.subTotal)}</Field>
            <Field label="Total">
              <span className="font-semibold text-gray-900">
                {PriceFormat(invoice.invoiceTotal)}
              </span>
            </Field>
            <Field label="Paid">{PriceFormat(invoice.amountPaid)}</Field>
            <Field label="Balance">
              <span className={balance > 0 ? "font-semibold text-amber-700" : "text-gray-900"}>
                {PriceFormat(balance)}
              </span>
            </Field>
            <Field label="Payment Method">
              <span className="inline-flex items-center gap-1">
                <CreditCardIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                {paymentMethodLabel[invoice.paymentMethod] ?? "-"}
              </span>
            </Field>
            {invoice.reference && (
              <Field label="Reference">{invoice.reference}</Field>
            )}
            {invoice.notes && (
              <div className="col-span-2 md:col-span-4">
                <Field label="Notes">{invoice.notes}</Field>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No invoice has been raised for this part yet.
          </p>
        )}
      </Card>

      {/* Buyer */}
      <Card
        title="Buyer"
        bodyClassName="p-4"
        headerRight={
          account ? (
            <a
              href={`/accounts/${account.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800"
            >
              Open Account
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ) : undefined
        }
      >
        {!account ? (
          <p className="text-sm text-gray-500">
            No buyer / account is linked to this sale.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Field label="Account">
                <span className="inline-flex items-center gap-2">
                  <BuildingOffice2Icon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  <span className="font-medium text-gray-900">{account.name}</span>
                  <span className="text-xs text-gray-500">#{account.accountNumber}</span>
                </span>
              </Field>
              <Field label="Primary Contact">
                {accountContacts.isLoading ? (
                  <LoadingWheel />
                ) : primaryContact ? (
                  <div className="flex flex-col">
                    <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">
                      <UserCircleIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                      {primaryContact.fullname}
                    </span>
                    {primaryContact.telephone1 && (
                      <a
                        href={`tel:${primaryContact.telephone1}`}
                        className="text-xs text-primary-700 hover:text-primary-800"
                      >
                        {primaryContact.telephone1}
                      </a>
                    )}
                    {primaryContact.email && (
                      <a
                        href={`mailto:${primaryContact.email}`}
                        className="text-xs text-primary-700 hover:text-primary-800"
                      >
                        {primaryContact.email}
                      </a>
                    )}
                  </div>
                ) : (
                  <span className="text-gray-400 italic">No primary contact</span>
                )}
              </Field>
              <Field label="VAT Number">{account.vatNumber || "-"}</Field>
            </div>
            <div className="space-y-3">
              <Field label="Billing Address">
                {addressLoading ? (
                  <LoadingWheel />
                ) : billingAddress ? (
                  getAddress(billingAddress)
                ) : (
                  <span className="text-gray-400 italic">N/A</span>
                )}
              </Field>
              <Field label="Default Shipping Address">
                {addressLoading ? (
                  <LoadingWheel />
                ) : shippingAddress ? (
                  <span className="inline-flex items-start gap-1.5">
                    <TruckIcon className="h-4 w-4 mt-0.5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                    <span>{getAddress(shippingAddress)}</span>
                  </span>
                ) : (
                  <span className="text-gray-400 italic">N/A</span>
                )}
              </Field>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
